import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { getEntitySettings, EntitySettings } from '../services/orgService';
import { formatDate as fmtDate, formatDateTime as fmtDateTime, EntityDateFormat } from '../utils/dateFormat';
import { formatNumber as fmtNum, formatCurrency as fmtCcy, EntityNumberFormat } from '../utils/numberFormat';

interface Ctx {
  settings: EntitySettings | null;
  dateFormat: EntityDateFormat;
  formatDate: (v: string | number | Date | null | undefined) => string;
  formatDateTime: (v: string | number | Date | null | undefined) => string;
  numberFormat: EntityNumberFormat;
  decimalPlaces: number;
  currencySymbol: string;
  currencyCode: string;
  formatNumber: (v: string | number | null | undefined) => string;
  formatCurrency: (v: string | number | null | undefined) => string;
  reload: () => void;
}

const EntitySettingsContext = createContext<Ctx | undefined>(undefined);

export const EntitySettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<EntitySettings | null>(null);

  const reload = useCallback(() => {
    getEntitySettings().then(setSettings).catch(() => setSettings(null));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const dateFormat = (settings?.date_format ?? 'DD-MM-YYYY') as EntityDateFormat;
  const formatDate = useCallback((v: string | number | Date | null | undefined) => fmtDate(v, dateFormat), [dateFormat]);
  const formatDateTime = useCallback((v: string | number | Date | null | undefined) => fmtDateTime(v, dateFormat), [dateFormat]);

  const numberFormat = (settings?.number_format ?? 'INDIAN') as EntityNumberFormat;
  const decimalPlaces = settings?.decimal_places ?? 2;
  const currencySymbol = settings?.currency_symbol ?? '₹';
  const currencyCode = settings?.currency_code ?? 'INR';
  const formatNumber = useCallback(
    (v: string | number | null | undefined) => fmtNum(v, { numberFormat, decimalPlaces }),
    [numberFormat, decimalPlaces]);
  const formatCurrency = useCallback(
    (v: string | number | null | undefined) => fmtCcy(v, { numberFormat, decimalPlaces, currencySymbol }),
    [numberFormat, decimalPlaces, currencySymbol]);

  return (
    <EntitySettingsContext.Provider value={{
      settings, dateFormat, formatDate, formatDateTime,
      numberFormat, decimalPlaces, currencySymbol, currencyCode, formatNumber, formatCurrency,
      reload,
    }}>
      {children}
    </EntitySettingsContext.Provider>
  );
};

// Returns { formatDate, formatDateTime, dateFormat, settings }. Safe fallback if used
// outside the provider so a screen never crashes — falls back to the default format.
export function useEntitySettings(): Ctx {
  const ctx = useContext(EntitySettingsContext);
  if (ctx) return ctx;
  return {
    settings: null,
    dateFormat: 'DD-MM-YYYY',
    formatDate: (v) => fmtDate(v, 'DD-MM-YYYY'),
    formatDateTime: (v) => fmtDateTime(v, 'DD-MM-YYYY'),
    numberFormat: 'INDIAN',
    decimalPlaces: 2,
    currencySymbol: '₹',
    currencyCode: 'INR',
    formatNumber: (v) => fmtNum(v, { numberFormat: 'INDIAN', decimalPlaces: 2 }),
    formatCurrency: (v) => fmtCcy(v, { numberFormat: 'INDIAN', decimalPlaces: 2, currencySymbol: '₹' }),
    reload: () => {},
  };
}

// Convenience: const fmt = useFormatDate();  <span>{fmt(row.created_on)}</span>
export function useFormatDate() {
  return useEntitySettings().formatDate;
}

export function useFormatNumber() {
  return useEntitySettings().formatNumber;
}

export function useFormatCurrency() {
  return useEntitySettings().formatCurrency;
}
